import React, { useState, useEffect } from 'react';
import {
  Alert,
  TextField,
  Button,
  Typography,
  Stack,
  InputLabel,
} from '@mui/material';

// UTIL
import {
  getCharacterCount,
  getMessageParts,
  getMessageMaxLength,
} from '../../utils/characterCount';

// MESSAGES
import TemplateFormattedMessages from '../../messages/websms/TemplateFormattedMessages';
import { ChevronLeft } from '@mui/icons-material';

const SingleView = ({
  selected,
  tab,
  handleSave,
  handleEdit,
  handleDelete,
  callBack,
  saveDisabled,
  errorHandler,
}) => {
  const [templateName, setTemplateName] = useState(
    selected ? selected.template_name : ''
  );
  const [templateContent, setTemplateContent] = useState(
    selected ? selected.template_text : ''
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTemplateName(selected ? selected.template_name : '');
    setTemplateContent(selected ? selected.template_text : '');
  }, [selected]);

  /*
    Handles state changes from the Form
  */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'templateName') {
      setTemplateName(value);
    } else if (name === 'templateContent') {
      setTemplateContent(value);
    }
  };

  const handleAccessLevel = () => {
    const { accessLevel, accessType } = selected || {};

    return (
      (accessLevel > '0' && accessType !== '0' && selected) || // if not Customer User
      (selected && tab === 'Personal') || // if Customer User edits Personal Template
      !selected
    ); // if Customer User add New Template (Global && Personal), allow fully access; else, show ReadOnly view
  };

  const handleSaveClick = async () => {
    if (!saving) {
      if (saveDisabled) {
        // do nothing
      } else {
        if (!selected && (!templateName || !templateContent)) {
          return setError('Must fill out both fields');
        }

        setSaving(true);

        try {
          // Editing Template
          if (selected) {
            await handleEdit(templateName, templateContent, errorHandler);
          }
          // Creating new Template
          else {
            await handleSave(templateName, templateContent, errorHandler);
          }
          callBack(); // Go back to the list
        } catch (e) {
          errorHandler(e);
        } finally {
          setSaving(false);
        }
      }
    }
  };

  const handleDeleteClick = async () => {
    if (selected) {
      await handleDelete(selected.template_sms_id, errorHandler);
      callBack(); // Go back to the list
    }
  };

  const handleCharacterLimitError = (characterCount, messageMaxLength) => {
    if (characterCount > messageMaxLength) {
      return (
        <Alert severity="error">
          Warning, maximum message size exceeded, please confirm message
          content.
        </Alert>
      );
    }
    return null;
  };

  const generateDeleteButton = () => {
    const { accessLevel, accessType } = selected || {};

    if (
      (accessLevel > '0' && accessType !== '0' && selected) ||
      (selected && tab === 'Personal')
    ) {
      return (
        <Button onClick={handleDeleteClick} variant="contained" color="error">
          {TemplateFormattedMessages.deleteText}
        </Button>
      );
    }
    return null;
  };

  const generateSaveAndCancelButton = (characterCount, messageMaxLength) => {
    const { accessLevel, accessType } = selected || {};

    if (
      (accessLevel > '0' && accessType !== '0' && selected) ||
      (selected && tab === 'Personal') ||
      !selected
    ) {
      return (
        <Stack sx={{ flexDirection: 'row', gap: 2 }}>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            color="secondary"
            disabled={saveDisabled || characterCount > messageMaxLength}
          >
            {saving
              ? TemplateFormattedMessages.saving
              : TemplateFormattedMessages.saveText}
          </Button>

          <Button onClick={callBack} variant="outlined" color="secondary">
            {TemplateFormattedMessages.cancelText}
          </Button>
        </Stack>
      );
    }
    return null;
  };

  const generateError = () => {
    if (!error) return null;

    return <Alert severity="error">{error}</Alert>;
  };

  const {
    standardCharacterOnly,
    unicodeCharacterOnly,
    characterCount,
    isUnicodeMessage,
  } = getCharacterCount(templateContent);

  const messagePartCount = getMessageParts(templateContent);
  const messageMaxLength = getMessageMaxLength(isUnicodeMessage);

  return (
    <div>
      <Button
        variant="text"
        onClick={callBack}
        startIcon={<ChevronLeft />}
        sx={{ marginBottom: 2 }}
        color="secondary"
      >
        {TemplateFormattedMessages.backButton}
      </Button>

      <Typography variant="h4" gutterBottom>
        {tab} {TemplateFormattedMessages.templateDetailsTitle}
      </Typography>

      <form>
        <Stack sx={{ marginBottom: 2 }}>
          <InputLabel htmlFor="templateName">
            {TemplateFormattedMessages.nameText}
          </InputLabel>

          <TextField
            onChange={handleChange}
            id="templateName"
            name="templateName"
            value={templateName}
            variant="outlined"
            fullWidth
            size="small"
            readOnly={handleAccessLevel()}
          />
        </Stack>

        <Stack>
          <InputLabel htmlFor="templateContent">
            {TemplateFormattedMessages.contentText}
          </InputLabel>

          <TextField
            onChange={handleChange}
            id="templateContent"
            name="templateContent"
            value={templateContent}
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            size="small"
            readOnly={handleAccessLevel()}
            slotProps={{
              htmlInput: { maxLength: messageMaxLength },
            }}
          />
        </Stack>

        <Typography>
          <Typography
            variant="span"
            fontWeight={700}
          >{`${characterCount}/${messageMaxLength} Character(s)`}</Typography>

          <Typography variant="span">
            {standardCharacterOnly > 0 &&
              ` = ${standardCharacterOnly} Standard Character(s)`}
            {unicodeCharacterOnly > 0 &&
              ` + ${unicodeCharacterOnly} Unicode Character(s)`}
          </Typography>
        </Typography>

        <Typography fontWeight={700}>
          {messagePartCount} Message Parts
        </Typography>

        {handleCharacterLimitError(characterCount, messageMaxLength)}
      </form>
      {generateError()}

      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginTop: 3,
        }}
      >
        {generateSaveAndCancelButton(characterCount, messageMaxLength)}
        {generateDeleteButton()}
      </Stack>
    </div>
  );
};

export default SingleView;
